import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const sizes = {
        sm: 'max-w-md',
        md: 'max-w-xl',
        lg: 'max-w-3xl',
        xl: 'max-w-5xl',
    };

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            {/* Heavy backdrop blur for dramatic depth */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-2xl transition-opacity duration-300"
                onClick={onClose}
            />

            {/* Modal Content with entry animation */}
            <div
                ref={modalRef}
                className={`
                    relative w-full ${sizes[size]}
                    bg-[#0C0C0C]/95 backdrop-blur-xl
                    border border-white/[0.06] rounded-2xl
                    shadow-[0_32px_64px_-12px_rgba(0,0,0,0.8)]
                    transform transition-all duration-300
                    ${isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}
                    flex flex-col max-h-[90vh]
                    overflow-hidden
                `}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
            >
                {/* Animated gradient top border */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-brand to-transparent opacity-60 animate-border-shimmer bg-[length:200%_100%]" />

                {/* Header */}
                <div className="flex items-center justify-between px-7 py-5 border-b border-white/[0.06]">
                    <h2
                        id="modal-title"
                        className="text-lg font-display font-semibold text-white"
                    >
                        {title}
                    </h2>

                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-white/[0.04] hover:bg-danger/[0.1] hover:text-danger text-white/50 transition-all duration-200 flex items-center justify-center hover:rotate-90"
                        aria-label="Close modal"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-7 py-6 overflow-y-auto">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};
